import _ from 'lodash';
import OneShotEventRegistry from 'app/shared/events/one_shot_event_registry';
import { ConfigService } from 'app/config/config.service';
import { PanesService } from 'app/main/PanesService';
import { Cesium } from 'app/cesium';
import { CesiumMap } from 'app/map/map';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import { TerrainService } from 'app/map/services/terrain.service';
import { ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { IDeferred, IQService, IRootScopeService, IWindowService } from 'angular';
import { UrlInitParams } from 'app/main/url_init_params.service';
import { PromisedTimeout } from 'app/shared/utilities/PromisedTimeout';
import { UserGeneratedConfigService } from 'app/config/user_generated_config.service';
import { PointGeometry } from 'app/agriculture/queries/types/geometry/PointGeometry';

// Value returned by MapService.getHeight when the cesium sample height function does not return
// within ConfigService.sampleHeightTimeoutMs
const INVALID_HEIGHT: number | null = null;

export class MapService {
    private readonly _initParams: UrlInitParams;
    private readonly _$rootScope: IRootScopeService;
    private readonly _$q: IQService;
    private readonly _terrainService: TerrainService;

    // Fires when the cesium viewer is loaded and ready
    private _viewerReadyEvent: OneShotEventRegistry;

    private _deferredViewerPromise: IDeferred<Cesium.Viewer>;
    private _userGeneratedConfigService: UserGeneratedConfigService;
    private _configService: ConfigService;

    public readonly viewer: Cesium.Viewer;

    /**
     * Main MapService service that contains the core Cesium interaction functions
     * and the initialisation. It is used to control the Cesium map globally.
     * @param initParams
     * @param $rootScope
     * @param $q
     * @param terrainService
     * @param cesiumMap
     * @param userGeneratedConfigService
     * @param configService
     */
    constructor(
        initParams: UrlInitParams,
        $rootScope: IRootScopeService,
        $q: IQService,
        terrainService: TerrainService,
        cesiumMap: CesiumMap,
        userGeneratedConfigService: UserGeneratedConfigService,
        configService: ConfigService,
    ) {
        this._initParams = initParams;
        this._$rootScope = $rootScope;
        this._$q = $q;
        this._terrainService = terrainService;
        this._configService = configService;
        this._userGeneratedConfigService = userGeneratedConfigService;

        this.viewer = cesiumMap.viewer;

        // Fires when the cesium viewer is loaded and ready
        this._viewerReadyEvent = new OneShotEventRegistry();
    }

    //// PUBLIC METHODS ////
    /**
     * The initialise function provides more control over the lifecycle
     * of this service, instead of relying on the angular to control
     * the construction, and hence initialisation, implicitly.
     *
     * @param element The html element to instantiate the Cesium canvas
     * existing implementation is UrlInitParams, that's used to initialise the
     * state of the map.
     **/
    initialise() {
        // Create and initialise the Cesium Viewer itself
        this.initialiseViewer();
        // Handle mouse input
        this._initialiseEventHandlers();
        // Handle terrain being disabled/enabled
        this._createTerrainEnabledListener();

        // HACK
        // Camera can be initialised either by a valid camera position
        // or by a valid farm id. We prefer a camera position if it exists.
        // See the counterpart in the ViewStateService.
        if (this._initParams.hasValidCameraPosition) {
            const cameraPos = this._initParams.cameraPosition;
            const cameraDir = this._initParams.cameraDirection;
            const cameraUp = this._initParams.cameraUp;
            this.flyCameraToPosition({
                destination: cameraPos,
                orientation: {
                    direction: cameraDir,
                    up: cameraUp,
                },
            });
        }

        const baseGlobeColor =
            this._userGeneratedConfigService.configData?.baseGlobeColor ||
            this._configService.baseGlobeColor;

        this.viewer.scene.globe.baseColor = Cesium.Color.fromBytes(
            baseGlobeColor[0],
            baseGlobeColor[1],
            baseGlobeColor[2],
        );

        this._viewerReadyEvent.notifyListeners(this.viewer);
    }

    addOnViewerReadyListener(listener) {
        this._viewerReadyEvent.addEventListener(listener);
    }

    flyCameraToPosition(viewpoint) {
        if (_.isNull(viewpoint.destination)) {
            throw 'Invalid viewpoint to fly to';
        }
        this.viewer.camera.flyTo(viewpoint);
    }

    initialiseViewer() {
        // Disable depth testing so things behind the terrain do not disappear (default used for 2D terrain)
        this.depthTestAgainstTerrain = false;

        if (this._terrainService.mapTerrainEnabled) {
            this.terrainProvider = this._terrainService.cesiumTerrainProvider;
        }

        //Set initial viewer position to Australia
        this.viewer.camera.setView({
            destination: Cesium.Rectangle.fromDegrees(115.0, -45.0, 145.0, -5.0),
        });

        //Change the resolution scaling
        if (this._configService.useDevicePixelRatio) {
            this.viewer.resolutionScale = window.devicePixelRatio;
            this.viewer.scene.postProcessStages.fxaa.enabled = false;
        }

        // Resolve our viewer promise
        if (this._deferredViewerPromise) {
            this._deferredViewerPromise.resolve(this.viewer);
        }
    }

    getViewer(): Promise<Cesium.Viewer> {
        if (this.viewer) {
            return Promise.resolve(this.viewer);
        } else {
            if (!this._deferredViewerPromise) {
                this._deferredViewerPromise = this._$q.defer();
            }
            return Promise.resolve().then(() => this._deferredViewerPromise.promise);
        }
    }

    /**
     * Takes mouse position, returns a latLng object
     * @param mouseX
     * @param mouseY
     */
    mousePositionToLocation(mouseX: number, mouseY: number): { lng?: number; lat?: number } {
        const pickRay = this.viewer.scene.camera.getPickRay(new Cesium.Cartesian2(mouseX, mouseY));
        const cartesian = this.viewer.scene.globe.pick(pickRay, this.viewer.scene);
        if (cartesian) {
            const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
            const lng = Cesium.Math.toDegrees(cartographic.longitude);
            const lat = Cesium.Math.toDegrees(cartographic.latitude);

            return { lng, lat };
        }
        return {};
    }

    getMapCameraPosition() {
        const { longitude, latitude, height } = this.viewer.camera.positionCartographic;
        const center = {
            type: 'Point',
            coordinates: [Cesium.Math.toDegrees(longitude), Cesium.Math.toDegrees(latitude)],
        } satisfies PointGeometry;

        return {
            center,
            viewAngle: Cesium.Math.toDegrees(this.viewer.camera.pitch),
            viewRotation: Cesium.Math.toDegrees(this.viewer.camera.heading),
            viewHeight: height,
        };
    }

    async getHeight(lng: number, lat: number): Promise<number> {
        //calculates the height of a point given a lat and long
        const position = [Cesium.Cartographic.fromDegrees(lng, lat)];

        // If terrain provider is null, instantly resolve with an invalid height
        if (!this._terrainService.cesiumTerrainProvider) {
            console.error('No terrain provider configured, unable to sample height');
            return Promise.resolve(INVALID_HEIGHT) as Promise<number>;
        }

        const promise = Cesium.sampleTerrain(
            this._terrainService.cesiumTerrainProvider,
            this._terrainService.terrainDetailLevel,
            position,
        );

        const cesiumPromise = Promise.resolve(promise)
            .then((updatedPosition) => {
                return updatedPosition[0].height;
            })
            .catch((e) => {
                console.log(e);
            });

        const timeoutPromise = new PromisedTimeout(
            this._configService.sampleHeightTimeoutMs,
            INVALID_HEIGHT,
        );

        return Promise.race([cesiumPromise, timeoutPromise.promise]);
    }

    disableDragging() {
        this.viewer.scene.screenSpaceCameraController.inertiaSpin = 0;
        this.viewer.scene.screenSpaceCameraController.enableRotate = false;
    }

    enableDragging() {
        this.viewer.scene.screenSpaceCameraController.inertiaSpin = 0.9;
        this.viewer.scene.screenSpaceCameraController.enableRotate = true;
    }

    //// PRIVATE METHODS ////

    //TODO refactor into Map Selections service
    _initialiseEventHandlers() {
        // Set zoom options to wheel and pinch only to remove right click scroll.
        this.viewer.scene.screenSpaceCameraController.zoomEventTypes = [
            Cesium.CameraEventType.WHEEL,
            Cesium.CameraEventType.PINCH,
        ];
    }

    /**
     * Sets whether or not depthTestAgainstTerrain is enabled for the viewer
     * This decides whether or not entities show/hide as they are obscured by terrain
     * @param depthTestAgainstTerrain Boolean that is used to set depthTestAgainstTerrain on the Cesium Viewer
     */
    set depthTestAgainstTerrain(depthTestAgainstTerrain) {
        this.viewer.scene.globe.depthTestAgainstTerrain = depthTestAgainstTerrain;
    }

    /**
     * Gets whether or not depthTestAgainstTerrain is enabled for the viewer
     */
    get depthTestAgainstTerrain() {
        return this.viewer.scene.globe.depthTestAgainstTerrain;
    }

    _createTerrainEnabledListener() {
        this._$rootScope.$on('terrainService:mapTerrainEnabledSet', (event, terrainEnabled) => {
            if (terrainEnabled) {
                this.depthTestAgainstTerrain = true;
                this.terrainProvider = this._terrainService.cesiumTerrainProvider;
            } else {
                this.depthTestAgainstTerrain = false;
                this.terrainProvider = new Cesium.EllipsoidTerrainProvider();
            }
        });
    }

    get terrainProvider(): Cesium.TerrainProvider {
        return this.viewer.terrainProvider;
    }

    set terrainProvider(newTerrainProvider: Cesium.TerrainProvider) {
        this.viewer.terrainProvider = newTerrainProvider;
    }
}
